import { Message } from '@/types/chat';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCheck } from 'lucide-react';

interface MessageBubbleProps {
    message: Message;
    isCurrentUser: boolean;
}

const MessageBubble = ({ message, isCurrentUser }: MessageBubbleProps) => {
    return (
        <div className={cn("flex w-full mt-1 space-x-3 max-w-[85%]", isCurrentUser ? "ml-auto justify-end" : "justify-start")}>
            {!isCurrentUser && (
                <Avatar className="h-6 w-6 mt-1 shrink-0">
                    <AvatarImage src={message.sender?.avatar_url || ''} />
                    <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">
                        {message.sender?.name?.charAt(0).toUpperCase() || '?'}
                    </AvatarFallback>
                </Avatar>
            )}
            <div
                className={cn(
                    "relative px-3 py-2 shadow-sm max-w-full break-words text-sm rounded-lg",
                    isCurrentUser
                        ? "bg-emerald-600 text-white dark:bg-emerald-700 rounded-tr-none"
                        : "bg-muted text-foreground dark:bg-muted/80 rounded-tl-none border border-border/20"
                )}
            >
                {!isCurrentUser && (
                    <span className="font-semibold text-xs mb-1 block text-primary">
                        {message.sender?.name}
                    </span>
                )}

                <div className="flex flex-wrap items-end gap-x-2 gap-y-0 relative">
                    <p className="leading-relaxed whitespace-pre-wrap mr-10 pb-1">{message.content}</p>

                    <div className="flex items-center gap-1 absolute bottom-0 right-0 ml-2 mb-1">
                        <span className={cn(
                            "text-[10px] select-none",
                            isCurrentUser ? "text-white/70" : "text-muted-foreground"
                        )}>
                            {format(new Date(message.created_at), 'HH:mm', { locale: ptBR })}
                        </span>
                        {isCurrentUser && (
                            <CheckCheck className="w-3.5 h-3.5 text-white/80" />
                        )}
                    </div>
                </div>
            </div>
            {/* CSS tail triangle could be added here for perfection, but rounded-tr-none implies it enough for now */}
        </div>
    );
};

export default MessageBubble;
